import { createWellnessMerger } from "./helperFunctions";

export class WellnessDataEngine {
  constructor() {
    this.merger = createWellnessMerger();
    this.data = { raw: [], byUserId: {} };
    this.periods = new Map();
    this._stats = { addedPeriods: 0 };
    this.loading = new Set();
    this.listeners = Object.create(null);
  }

  on(event, fn) {
    if (!this.listeners[event]) {
      this.listeners[event] = new Set();
    }

    this.listeners[event].add(fn);
    return () => this.off(event, fn);
  }

  off(event, fn) {
    this.listeners[event]?.delete(fn);
  }

  emit(event, payload) {
    const fns = this.listeners[event];
    if (!fns) {
      return;
    }

    for (const fn of fns) {
      fn(payload);
    }
  }

  isLoading(key) {
    return this.loading.has(key);
  }

  async guardLoad(key, loaderFn) {
    if (this.periods.has(key)) {
      return { status: "already-loaded" };
    }

    if (this.loading.has(key)) {
      return { status: "already-loading" };
    }

    this.loading.add(key);
    this.emit("loading-start", { key });

    try {
      const incoming = await loaderFn();

      if (!incoming) {
        this.loading.delete(key);
        this.emit("loading-end", { key, empty: true });
        return { status: "empty" };
      }

      this.merger(this.data, incoming);
      this.periods.set(key, true);
      this._stats.addedPeriods += 1;

      this.loading.delete(key);
      this.emit("period-loaded", { key, incoming });
      this.emit("loaded-end", { key });

      return { status: "loaded", incoming };
    } catch (e) {
      this.loading.delete(key);
      this.emit("loading-error", { key, error: e });

      throw e;
    }
  }

  reset() {
    this.merger.reset();
    this.data = { raw: [], byUserId: {} };
    this.periods.clear();
    this._stats = { addedPeriods: 0 };
  }

  getAllRaw() {
    // this data should be read-only. Please don't mutate it
    return this.data.raw;
  }

  getByUserId() {
    return this.data.byUserId;
  }

  stats() {
    const mergerStats = this.merger.stats?.() || {};
    return {
      ...this._stats,
      ...mergerStats,
      totalMerged: this.data.raw.length,
    };
  }

  findInRaw(predicate) {
    const out = [];
    const raw = this.data.raw || [];

    for (let i = 0; i < raw.length; i++) {
      const r = raw[i];
      if (predicate(r)) {
        out.push(r);
      }
    }

    return out;
  }
}

export default WellnessDataEngine;
